/**
 * RaceResultListItem — item compacto para la sección "Pendientes de
 * reportar" dentro de la pestaña Resultados de RacesPage (v16.3.1 R.1 +
 * v16.4.1 §T4).
 *
 * Representa una carrera ya pasada (race_date <= today) cuyo atleta NO
 * ha registrado resultado aún (`completed=false`). Muestra info mínima
 * + CTA para abrir RaceResultModal.
 *
 * El gate de visibilidad lo aplica el padre (`ResultsTabContent`).
 * Este componente NO valida; solo renderiza. Defensa light: si por
 * race condition se le pasa un race con `completed=true`, igual lo
 * renderiza (es una decisión del padre; aquí no rechazamos).
 *
 * Pattern paralelo a RaceCard pero MÁS COMPACTO + énfasis en CTA único.
 */
import { distanceLabel } from "@/components/races/distanceLabel";
import { t } from "@/i18n/es";
import type { RaceTarget } from "@/types/api";

export interface RaceResultListItemProps {
  race: RaceTarget;
  onAddResult: (id: number) => void;
}

function _formatRaceDate(iso: string): string {
  // Patrón anclado del repo: `T00:00:00` fuerza interpretación local
  // para evitar TZ drift de un día en formatos relativos.
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function RaceResultListItem({
  race,
  onAddResult,
}: RaceResultListItemProps) {
  const name = race.name ?? distanceLabel(race.distance);
  return (
    <article
      data-testid={`race-result-pending-${race.id}`}
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-carbon-raised p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-bone">{name}</p>
        <p className="text-xs text-bone-muted">
          {_formatRaceDate(race.race_date)} · {distanceLabel(race.distance)}
        </p>
      </div>
      <button
        type="button"
        data-testid={`race-result-pending-cta-${race.id}`}
        onClick={() => onAddResult(race.id)}
        className="self-start rounded-md bg-lime px-3 py-1.5 text-xs font-medium text-graphite hover:bg-lime/90 sm:self-auto"
      >
        {t.races.cardAddResultAction}
      </button>
    </article>
  );
}
